/**
 * TanStack Router route tree
 *
 * Auth protection uses layout routes (id-only routes without a path) that
 * check the Zustand auth store before rendering children. Once Phase 1
 * (Clerk auth forms) is complete this can be upgraded to beforeLoad() with
 * the Clerk context for fully type-safe, server-style protection.
 *
 * Route groups:
 *   __root        → mounts ViteAppProviders + <Outlet />
 *   /_guest/*     → redirect to / if already signed in
 *   /_protected/* → redirect to /login if not signed in
 *   public        → pricing, auth callbacks
 */
import { lazy, Suspense, useEffect } from 'react'
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  useNavigate,
} from '@tanstack/react-router'
import { useAuth as useClerkAuth } from '@clerk/clerk-react'
import { PageLoadingSkeleton } from './components/page-loading-skeleton'
import { AppLayout } from '@/components/layout/app-layout'
import { useAuthStore } from '@/stores/auth-store'

// ─── Lazy page imports ────────────────────────────────────────────────────────

const LoginPage = lazy(() => import('./pages/login'))
const SignupPage = lazy(() => import('./pages/signup'))
const ForgotPasswordPage = lazy(() => import('./pages/forgot-password'))
const ResetPasswordPage = lazy(() => import('./pages/reset-password'))

const PricingPage = lazy(() => import('./pages/pricing'))
const NotFoundPage = lazy(() => import('./pages/not-found'))

const DashboardPage = lazy(() => import('./pages/dashboard'))
const TasksPage = lazy(() => import('./pages/tasks'))
const CalendarPage = lazy(() => import('./pages/calendar'))
const TimerPage = lazy(() => import('./pages/timer'))
const InsightsPage = lazy(() => import('./pages/insights'))
const ProjectsPage = lazy(() => import('./pages/projects'))
const ProjectDetailPage = lazy(() => import('./pages/project-detail'))
const SettingsPage = lazy(() => import('./pages/settings'))
const FeedbackPage = lazy(() => import('./pages/feedback'))
const PaymentSuccessPage = lazy(() => import('./pages/payment-success'))
const PaymentFailedPage = lazy(() => import('./pages/payment-failed'))

// ─── Layout components ────────────────────────────────────────────────────────

function ProtectedLayout() {
  const { user, isLoading } = useAuthStore()
  // While a Clerk session exists but the store user hasn't resolved yet (the
  // bridge is fetching /api/user/me), WAIT — redirecting to /login would loop:
  // Clerk's <SignIn> sees the active session and bounces straight back here.
  const { isLoaded: clerkLoaded, isSignedIn: clerkSignedIn } = useClerkAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (!isLoading && !user && clerkLoaded && !clerkSignedIn) {
      const next = window.location.pathname + window.location.search
      navigate({ to: '/login', search: { next } })
    }
  }, [user, isLoading, clerkLoaded, clerkSignedIn, navigate])

  if (isLoading || !user) return <PageLoadingSkeleton />

  return (
    <Suspense fallback={<PageLoadingSkeleton />}>
      <Outlet />
    </Suspense>
  )
}

/**
 * The application chrome — header, sidebar, command palette, onboarding.
 *
 * Every page used to render `<AppLayout>` itself, twice or three times where
 * the page had loading branches, and a page that forgot lost the sidebar and
 * header entirely with nothing to catch it. `/insights` shipped that way.
 * Mounting it here makes the chrome a property of the route rather than
 * something each page has to remember.
 *
 * Not every protected route wants it: the payment result pages are full-page
 * `min-h-screen` cards that would scroll inside the shell's own scroll
 * container, so they stay children of `ProtectedLayout` instead.
 */
function AppShellLayout() {
  return (
    <AppLayout>
      <Outlet />
    </AppLayout>
  )
}

function GuestLayout() {
  const { user, isLoading } = useAuthStore()
  const navigate = useNavigate()

  useEffect(() => {
    if (!isLoading && user) {
      const params = new URLSearchParams(window.location.search)
      const next = params.get('next') ?? '/'
      navigate({ to: next as '/' })
    }
  }, [user, isLoading, navigate])

  if (isLoading) return <PageLoadingSkeleton />
  if (user) return null

  return (
    <Suspense fallback={<PageLoadingSkeleton />}>
      <Outlet />
    </Suspense>
  )
}

// ─── Route tree ───────────────────────────────────────────────────────────────

// Runtime components use router hooks (useRouter/usePathname/useSearchParams via
// the next/navigation shim), so they must mount inside the router tree — mounting
// them in ViteAppProviders (outside RouterProvider) crashes the app on boot.
const MiniTimer = lazy(() =>
  import('@/components/timer/mini-timer').then((m) => ({ default: m.MiniTimer })),
)
const AutoStartHandler = lazy(() =>
  import('@/components/timer/auto-start-handler').then((m) => ({
    default: m.AutoStartHandler,
  })),
)
const TimerRuntime = lazy(() =>
  import('@/components/timer/timer-runtime').then((m) => ({ default: m.TimerRuntime })),
)
const NotificationRuntime = lazy(() =>
  import('@/components/shared/notification-runtime').then((m) => ({
    default: m.NotificationRuntime,
  })),
)

const rootRoute = createRootRoute({
  component: () => (
    <>
      <Suspense fallback={<PageLoadingSkeleton />}>
        <Outlet />
      </Suspense>
      <Suspense fallback={null}>
        <NotificationRuntime />
        <MiniTimer />
        <AutoStartHandler />
        <TimerRuntime />
      </Suspense>
    </>
  ),
  notFoundComponent: () => <NotFoundPage />,
})

// Guest-only layout (auth pages)
const guestLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'guest',
  component: GuestLayout,
})

const loginRoute = createRoute({
  getParentRoute: () => guestLayoutRoute,
  path: '/login',
  component: () => <LoginPage />,
})

const signupRoute = createRoute({
  getParentRoute: () => guestLayoutRoute,
  path: '/signup',
  component: () => <SignupPage />,
})

const forgotPasswordRoute = createRoute({
  getParentRoute: () => guestLayoutRoute,
  path: '/forgot-password',
  component: () => <ForgotPasswordPage />,
})

const resetPasswordRoute = createRoute({
  getParentRoute: () => guestLayoutRoute,
  path: '/reset-password',
  component: () => <ResetPasswordPage />,
})

// Public routes
const pricingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pricing',
  component: () => <PricingPage />,
})


// Protected layout (requires auth)
const protectedLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'protected',
  component: ProtectedLayout,
})

// Everything with app chrome hangs off this; see AppShellLayout.
const appShellRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  id: 'app-shell',
  component: AppShellLayout,
})

const dashboardRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/',
  component: () => <DashboardPage />,
})

const tasksRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/tasks',
  component: () => <TasksPage />,
})

const calendarRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/calendar',
  component: () => <CalendarPage />,
})

const timerRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/timer',
  component: () => <TimerPage />,
})

const insightsRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/insights',
  component: () => <InsightsPage />,
})

const projectsRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/projects',
  component: () => <ProjectsPage />,
})

const projectDetailRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/projects/$id',
  component: () => <ProjectDetailPage />,
})

const settingsRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/settings',
  component: () => <SettingsPage />,
})

const feedbackRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/feedback',
  component: () => <FeedbackPage />,
})

const paymentSuccessRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/payment/success',
  component: () => <PaymentSuccessPage />,
})

const paymentFailedRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/payment/failed',
  component: () => <PaymentFailedPage />,
})





// ─── Router instance ──────────────────────────────────────────────────────────

const routeTree = rootRoute.addChildren([
  guestLayoutRoute.addChildren([
    loginRoute,
    signupRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
  ]),
  pricingRoute,
  protectedLayoutRoute.addChildren([
    appShellRoute.addChildren([
      dashboardRoute,
      tasksRoute,
      calendarRoute,
      timerRoute,
      insightsRoute,
      projectsRoute,
      projectDetailRoute,
      settingsRoute,
      feedbackRoute,
    ]),
    // Deliberately outside the shell — see AppShellLayout.
    paymentSuccessRoute,
    paymentFailedRoute,
  ]),
])

export const router = createRouter({ routeTree })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
